import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Shield, Monitor, CheckCircle, XCircle, RefreshCw, ArrowLeft, Layers } from 'lucide-react';
import { useData } from '../contexts/DataContext';

const KioskAuthorizePage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { plants } = useData();
  const [device, setDevice] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authorizing, setAuthorizing] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [plantId, setPlantId] = useState('');
  const [profileId, setProfileId] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [devRes, profRes] = await Promise.all([
          api.get(`/kiosk/device-info/${code}`),
          api.get('/kiosk/profiles')
        ]);
        setDevice(devRes.data);
        setDeviceName(devRes.data.device_name || '');
        setProfiles(profRes.data);
        if (devRes.data.status === 'approved') {
          setAuthorized(true);
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Dispozitiv negasit');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [code]);

  const handleAuthorize = async () => {
    setAuthorizing(true);
    try {
      await api.post('/kiosk/authorize-device', {
        device_code: code,
        device_name: deviceName,
        plant_id: plantId,
        profile_id: profileId
      });
      setAuthorized(true);
      toast.success('Dispozitiv autorizat cu succes!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la autorizare');
    } finally {
      setAuthorizing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-slate-900 mb-2">Eroare</h2>
            <p className="text-slate-500 mb-4">{error}</p>
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Inapoi la Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (authorized) {
    const selectedProfile = profiles.find(p => p.id === profileId);
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md w-full" data-testid="kiosk-authorized-card">
          <CardContent className="pt-6 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Dispozitiv Autorizat!</h2>
            <p className="text-slate-500 mb-1">Ecranul kiosk va afisa automat dashboard-ul.</p>
            {selectedProfile && (
              <p className="text-sm text-blue-600 mb-1">
                <Layers className="w-3.5 h-3.5 inline mr-1" />
                Profil: <strong>{selectedProfile.name}</strong>
              </p>
            )}
            <p className="text-sm text-slate-400 mb-6">
              Cod: <span className="font-mono font-bold text-blue-600">{code}</span>
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate('/admin/kiosk-devices')} data-testid="manage-devices-btn">
                <Monitor className="w-4 h-4 mr-2" /> Gestioneaza
              </Button>
              <Button onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Card className="max-w-md w-full" data-testid="kiosk-authorize-form">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Shield className="w-8 h-8 text-blue-600" />
          </div>
          <CardTitle className="text-xl">Autorizare Kiosk</CardTitle>
          <p className="text-sm text-slate-500 mt-1">Un ecran kiosk solicita acces la dashboard</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-slate-50 rounded-lg p-4 text-center">
            <p className="text-xs text-slate-500 mb-1">Cod Dispozitiv</p>
            <p className="text-2xl font-mono font-bold text-blue-600 tracking-widest">{code}</p>
            <p className="text-xs text-slate-400 mt-1">
              Inregistrat: {device?.created_at ? new Date(device.created_at).toLocaleString('ro-RO') : '-'}
            </p>
          </div>

          <div className="space-y-2">
            <Label>Nume Dispozitiv</Label>
            <Input value={deviceName} onChange={e => setDeviceName(e.target.value)} placeholder="ex: TV Hala Productie 1" data-testid="kiosk-device-name-input" />
          </div>

          <div className="space-y-2">
            <Label>Profil Kiosk</Label>
            <Select value={profileId || 'none'} onValueChange={v => setProfileId(v === 'none' ? '' : v)}>
              <SelectTrigger data-testid="kiosk-profile-select">
                <SelectValue placeholder="Selecteaza profil" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Fara profil (afiseaza tot)</SelectItem>
                {profiles.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    <div className="flex items-center gap-2">
                      <Layers className="w-3.5 h-3.5 text-blue-500" />
                      {p.name}
                      <span className="text-xs text-slate-400">({(p.slides || []).filter(s => s.enabled).length} slide-uri)</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {profiles.length === 0 && (
              <p className="text-xs text-orange-500">Niciun profil creat. Toate slide-urile vor fi afisate.</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Fabrica (optional)</Label>
            <Select value={plantId || 'all'} onValueChange={v => setPlantId(v === 'all' ? '' : v)}>
              <SelectTrigger data-testid="kiosk-plant-select"><SelectValue placeholder="Toate fabricile" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate fabricile</SelectItem>
                {(plants || []).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <Button className="w-full bg-green-600 hover:bg-green-700" onClick={handleAuthorize} disabled={authorizing} data-testid="kiosk-authorize-btn">
            {authorizing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            Autorizeaza Dispozitivul
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default KioskAuthorizePage;
